import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider as AppThemeProvider, useTheme } from './contexts/ThemeContext';
import AuthLayout from './components/layout/AuthLayout';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import AuthAction from './components/auth/AuthAction';
import LandingPage from './components/landing/LandingPage';
import Contact from './components/public/Contact';
import Portfolio from './components/public/Portfolio';
import Dashboard from './components/dashboard/Dashboard';
import UserManagement from './components/admin/UserManagement';

import Profile from './components/user/Profile';
import Settings from './components/user/Settings';
import ProjectWorkspace from './components/project/ProjectWorkspace';
import { hasProjectWorkspaceAccess } from './utils/roles';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';

import About from './pages/About';
import AIAutomationIndia from './pages/AIAutomationIndia';
import NotFound from './pages/NotFound';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './style/globals.css';

import PublicLayout from './components/layout/PublicLayout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import usePageTracking from './hooks/usePageTracking';
import { SidebarProvider } from './contexts/SidebarContext';

// Dynamic MUI Theme Wrapper connecting to App Theme Context
function MuiThemeWrapper({ children }) {
  const { themeMode } = useTheme();

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          primary: {
            main: '#06b6d4',
            light: '#22d3ee',
            dark: '#0891b2',
          },
          secondary: {
            main: '#10b981',
          },
          background: {
            default: themeMode === 'light' ? '#f8fafc' : '#0b0c0e',
            paper: themeMode === 'light' ? '#ffffff' : '#131418',
          },
          text: {
            primary: themeMode === 'light' ? '#0f172a' : '#f7f7f8',
            secondary: themeMode === 'light' ? '#64748b' : '#94a3b8',
          },
        },
        typography: {
          fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
                fontWeight: 600,
              },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: themeMode === 'light' ? '#f8fafc' : '#0b0c0e',
                color: themeMode === 'light' ? '#0f172a' : '#f7f7f8',
              },
            },
          },
        },
      }),
    [themeMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

// Client Project Route Protection
function ClientProjectRoute() {
  const { currentUser, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner fullScreen message="Loading workspace..." />;
  }
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  if (!hasProjectWorkspaceAccess(currentUser)) {
    return <Navigate to="/profile" replace />;
  }
  return <ProjectWorkspace />;
}

// Smart Dashboard Route - routes clients to /project and users to /profile
function DashboardRoute() {
  const { currentUser, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner fullScreen message="Loading..." />;
  }
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  if (hasProjectWorkspaceAccess(currentUser)) {
    return <Navigate to="/project" replace />;
  }
  return <Navigate to="/profile" replace />;
}

// Analytics Tracker Component
function AnalyticsTracker() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <AppThemeProvider>
        <MuiThemeWrapper>
          <Router>
            <AnalyticsTracker />
            <AuthProvider>
              <NotificationProvider>
                <SidebarProvider>
                  <Routes>
                    {/* Public routes wrapped in PublicLayout */}
                    <Route element={<PublicLayout />}>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/launchpad" element={<LandingPage isStandaloneLaunchpad={true} />} />
                      <Route path="/startup-launchpad" element={<LandingPage isStandaloneLaunchpad={true} />} />
                      <Route path="/launchpad/wizard" element={<LandingPage isStandaloneLaunchpad={true} openWizardImmediately={true} />} />
                      <Route path="/resume/:leadId" element={<LandingPage isStandaloneLaunchpad={true} />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/sign-in" element={<Navigate to="/login" replace />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/sign-up" element={<Navigate to="/signup" replace />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/auth/action" element={<AuthAction />} />
                      <Route path="/__/auth/action" element={<AuthAction />} />
                      <Route path="/newsletter" element={<Navigate to="/blog" replace />} />

                      <Route path="/portfolio" element={<Portfolio />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/how-it-works" element={<Navigate to="/#how-it-works" replace />} />
                      <Route path="/ai-automation-agency-india" element={<AIAutomationIndia />} />

                      {/* Legacy redirects */}
                      <Route path="/tools" element={<Navigate to="/" replace />} />
                      <Route path="/tools/*" element={<Navigate to="/" replace />} />
                      <Route path="/top-tools" element={<Navigate to="/" replace />} />
                      <Route path="/best-ai-tools" element={<Navigate to="/" replace />} />
                      <Route path="/ai-apps" element={<Navigate to="/launchpad" replace />} />
                      <Route path="/ai-apps/*" element={<Navigate to="/launchpad" replace />} />
                      <Route path="/aiapps" element={<Navigate to="/launchpad" replace />} />
                      <Route path="/aiapps/*" element={<Navigate to="/launchpad" replace />} />

                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/blog" element={<BlogList />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                    </Route>

                    {/* Standalone Pages (No Header/Footer) */}
                    <Route path="/contact" element={<Contact />} />

                    {/* Protected routes wrapped in AuthLayout */}
                    <Route element={<AuthLayout />}>
                      <Route path="/dashboard" element={<DashboardRoute />} />
                      <Route path="/project" element={<ClientProjectRoute />} />
                      <Route path="/workspace" element={<ClientProjectRoute />} />
                      <Route path="/invoices" element={<Navigate to="/project?tab=invoices" replace />} />
                      <Route path="/billing" element={<Navigate to="/project?tab=invoices" replace />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/settings" element={<Settings />} />

                      {/* Legacy redirects */}
                      <Route path="/research/ainewsletter" element={<Navigate to="/profile" replace />} />
                      <Route path="/admin/*" element={<Navigate to="/profile" replace />} />
                    </Route>

                    {/* 404 Catch-all route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <ToastContainer position="bottom-right" />
                </SidebarProvider>
              </NotificationProvider>
            </AuthProvider>
          </Router>
        </MuiThemeWrapper>
      </AppThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
